/**
 * Keeps a mapping of ArtworkLayer.id -> HTMLImageElement and owns the
 * object URLs used as image sources. Call `release` when a layer is
 * removed to free the blob URL and prevent memory leaks.
 */
export class ImageRegistry {
  private images = new Map<string, HTMLImageElement>();
  private owned = new Map<string, string>(); // layerId -> objectURL we created

  get(id: string): HTMLImageElement | undefined {
    return this.images.get(id);
  }

  map(): Map<string, HTMLImageElement> {
    return this.images;
  }

  /**
   * Load a File into an HTMLImageElement and register it.
   * Returns the created object URL, natural size, and the image element.
   */
  async loadFromFile(
    layerId: string,
    file: File,
  ): Promise<{
    imageSrc: string;
    naturalWidth: number;
    naturalHeight: number;
  }> {
    const url = URL.createObjectURL(file);
    try {
      const img = await loadImageElement(url);
      this.images.set(layerId, img);
      this.owned.set(layerId, url);
      return {
        imageSrc: url,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
      };
    } catch (err) {
      URL.revokeObjectURL(url);
      throw err;
    }
  }

  /**
   * Map `newId` to the same HTMLImageElement already registered under
   * `sourceId`. Does NOT take ownership of the source's object URL, so
   * releasing the new id is a no-op and the original URL stays alive
   * until the source layer itself is released. Used when duplicating a
   * layer so we don't re-fetch / re-decode the bitmap.
   */
  shareTo(sourceId: string, newId: string): boolean {
    const img = this.images.get(sourceId);
    if (!img) return false;
    this.images.set(newId, img);
    return true;
  }

  release(layerId: string): void {
    this.images.delete(layerId);
    const url = this.owned.get(layerId);
    if (url) {
      URL.revokeObjectURL(url);
      this.owned.delete(layerId);
    }
  }

  clear(): void {
    for (const [, url] of this.owned) URL.revokeObjectURL(url);
    this.owned.clear();
    this.images.clear();
  }
}

export function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

export const ACCEPTED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
];

export function isAcceptedImage(file: File): boolean {
  if (file.type && ACCEPTED_IMAGE_TYPES.includes(file.type)) return true;
  // Fallback to extension sniff.
  const name = file.name.toLowerCase();
  return (
    name.endsWith(".png") ||
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg") ||
    name.endsWith(".webp") ||
    name.endsWith(".gif")
  );
}
